import React, { useState, useEffect } from 'react';
import {
  Button,
  DialogContent,
  DialogProps,
  DatePicker,
  Input,
  Textarea,
  palettes,
  Select,
  Theme,
  useFlashMessage,
  SelectChangeEvent
} from '@indigo-ag/component-module';
import { MaterialUiPickersDate } from 'material-ui-pickers';
import i18next from 'core/i18n';
import {
  AgFinancingFile,
  AgFinancingDebtType,
  Debt,
  AgFinancingFilesContext,
  AgFinancingFileInput,
  CreditInput
} from '@indigo-ag/schema';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { EnhancedDialog, UploadDocument, createStylesHook, debtTypesOptions, utils, ModalActionButtons } from 'shared';
import {
  agFinancingFilesToFiles,
  getDebtTypeTranslated,
  ProgressDictionary,
  removeFilesFromAgFinancing,
  updateFilesAgFinancingFiles,
  uploadFilesToAgFinancing
} from '../../../../../utils';
import { PesquisaSerasaProps } from '../../PesquisaSerasa';
import { BrazilCurrencySymbol } from '../../../../../shared/BrazilCurrencySymbol/BrazilCurrencySymbol';
import { currency } from 'shared/utils';
import { ApolloError } from '@apollo/client';

const useStyles = createStylesHook((theme: Theme) => ({
  flexDocument: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  inputStyle: {
    marginRight: '32px !important'
  },
  size14Black: {
    fontSize: theme.typography.pxToRem(14),
    color: palettes.neutral['700']
  }
}));

export interface PesquisaSerasaModalProps extends DialogProps, PesquisaSerasaProps {
  closeModal: () => void;
  // TODO create type for function
  updateCredit: (newCreditInfo: Partial<CreditInput>) => Promise<unknown>;
  toUploadFiles?: ReturnType<typeof uploadFilesToAgFinancing>;
}

export const PesquisaSerasaModal: React.FunctionComponent<PesquisaSerasaModalProps> = ({
  closeModal,
  pesquisaSerasaInfo,
  open,
  updateCredit,
  toUploadFiles
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * State
   */
  const [consultationDate, setConsultationDate] = useState(pesquisaSerasaInfo?.serasaConsultationDate || null);
  const [option, setOption] = useState<AgFinancingDebtType | null>(null);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState(0);
  const [observationDescription, setObservationDescription] = useState(pesquisaSerasaInfo?.serasaObservation || '');
  const [allFiles, setAllFiles] = useState<Partial<AgFinancingFile>[]>(pesquisaSerasaInfo?.files || []);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [filesIdsToRemove, setFilesIdsToRemove] = useState<AgFinancingFile['id'][]>([]);
  const [debts, setDebts] = useState<Debt[]>(pesquisaSerasaInfo?.serasaDebts || []);
  const [progress, setProgress] = useState<ProgressDictionary>();
  const [filesUpdated, setFilesUpdated] = useState<AgFinancingFile[]>([]);

  const handleSetDate = (date: MaterialUiPickersDate) => setConsultationDate(date);

  const handleSetObservationDescription = (e: React.ChangeEvent<any>) => setObservationDescription(e.target.value);

  const handleChange = (event: SelectChangeEvent<AgFinancingDebtType>): void => {
    setOption(event.target.value);
  };
  const handleName = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setName(event.target.value);
  };

  const handleAmount = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setAmount(parseFloat(event.target.value));
  };

  const handleAddDebts = (): void => {
    setDebts([
      ...debts,
      {
        amount,
        name,
        type: option as AgFinancingDebtType
      }
    ]);
    setAmount(0);
    setOption(null);
    setName('');
  };

  const handleRemoveDebt = (debt: number) => {
    return () => {
      const newDebts = debts.filter((_, index) => index !== debt);
      setDebts(newDebts);
    };
  };

  const addNewFiles = (files: File[]) => {
    setNewFiles([...newFiles, ...files]);

    setAllFiles([
      ...allFiles,
      ...files.map(f => ({
        ...f,
        documentName: f.name
      }))
    ]);
  };

  const fileReplacer = (file: File) => {
    const fileIndex = newFiles.findIndex(f => f['newFileId'] === file['newFileId']);
    const replacedFiles = [...newFiles];
    replacedFiles[fileIndex]['comment'] = file['comment'];
    setNewFiles(replacedFiles);
  };

  const onUpdateFile = (file: File) => {
    updateFilesAgFinancingFiles(file, allFiles, filesUpdated, setFilesUpdated);
  };

  const addFileToRemove = (fileIdx: number) => {
    const updatedIbamaFiles = [...allFiles];
    const fileToRemove = updatedIbamaFiles.splice(fileIdx, 1)[0];

    if (fileToRemove.id) {
      setFilesIdsToRemove([...filesIdsToRemove, fileToRemove.id]);
    } else {
      const updatedNewFiles = [...newFiles];
      const newFileToRemoveIdx = fileIdx - (pesquisaSerasaInfo?.files ? pesquisaSerasaInfo.files.length : 0);
      updatedNewFiles.splice(newFileToRemoveIdx, 1);
      setNewFiles(updatedNewFiles);
    }

    setAllFiles(updatedIbamaFiles);
  };

  const onCleanState = () => {
    setConsultationDate(pesquisaSerasaInfo?.serasaConsultationDate || null);
    setObservationDescription(pesquisaSerasaInfo?.serasaObservation || '');
    setAllFiles(pesquisaSerasaInfo?.files || []);
    setNewFiles([]);
    setFilesIdsToRemove([]);
    setOption(null);
    setName('');
    setAmount(0);
    setDebts(pesquisaSerasaInfo?.serasaDebts || []);
    setFilesUpdated([]);
  };

  useEffect(() => {
    onCleanState();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAddBtnDisable = !name || !amount || !option;

  const { addFlashMessage } = useFlashMessage();

  const updateInfo = () => {
    const newDebts: Debt[] = [...debts];
    if (!isAddBtnDisable) {
      newDebts.push({
        amount,
        name,
        type: option as AgFinancingDebtType
      });
    }

    const fileFlow = toUploadFiles
      ? removeFilesFromAgFinancing(filesIdsToRemove).then(() =>
          toUploadFiles(newFiles, AgFinancingFilesContext.PESQUISA_SERASA, undefined, progressCompleted =>
            setProgress(progressCompleted)
          )
        )
      : Promise.resolve({ fulfilled: [], rejected: [] });

    return fileFlow
      .then(filesUploaded =>
        updateCredit({
          serasaConsultationDate: consultationDate!,
          serasaObservation: observationDescription,
          serasaDebts: utils.graphql.removeTypename(newDebts),
          files: filesUploaded.fulfilled as AgFinancingFileInput[]
        })
      )
      .then(() => {
        // TODO: translate Pesquisa Serasa?
        addFlashMessage({
          content: `Pesquisa Serasa ${i18next.t('hasBeenUpdatedSuccessfully')}`,
          title: '',
          variant: 'success'
        });
        setProgress({});
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0]?.message || i18next.t('error'),
          title: '',
          variant: 'error'
        });
      });

    /* return removeFilesFromAgFinancing(filesIdsToRemove)
      .then(() => {
        return uploadFilesToPhase(newFiles, AgFinancingFilesContext.PESQUISA_SERASA, undefined, progressCompleted =>
          setProgress(progressCompleted)
        );
      })
      .then(filesUploaded => {
        return updateCredit(
          {
            serasaConsultationDate: consultationDate!,
            serasaObservation: observationDescription,
            serasaDebts: utils.graphql.removeTypename(newDebts)
          },
          filesUploaded as AgFinancingFile[]
        );
      })
      .then(() => {
        const result = filesUpdated.map((updatedFile: AgFinancingFile, i) => ({
          id: updatedFile.id,
          metadata: {
            comment: updatedFile.metadata?.comment
          }
        }));
        return updateAgFinancingFileFn({
          variables: {
            data: result
          }
        });
      })
      .then(() => {
        // TODO: translate Pesquisa Serasa?
        addFlashMessage({
          content: `Pesquisa Serasa ${i18next.t('hasBeenUpdatedSuccessfully')}`,
          title: '',
          variant: 'success'
        });
        setProgress({});
      }); */
  };

  return (
    <EnhancedDialog open={open} onClose={closeModal} title="Pesquisa Serasa" dialogMaxWidth="480px">
      <DialogContent>
        <DatePicker
          label={i18next.t('consultationDate')}
          name="dueDate"
          onChange={handleSetDate}
          value={consultationDate}
        />
        <Select
          label={`${i18next.t('debtType')} (${i18next.t('optional')})`}
          name="debt"
          options={debtTypesOptions}
          onChange={handleChange}
          value={option}
          placeholder={i18next.t('pleaseSelect')}
        />
        {(option || debts.length > 0) && (
          <>
            <div className={classes.flexDocument}>
              <Input label={i18next.t('name')} className={classes.inputStyle} value={name} onChange={handleName} />
              <Input
                label={i18next.t('amount')}
                value={isNaN(amount) ? '' : String(amount)}
                onChange={handleAmount}
                renderBeforeInput={BrazilCurrencySymbol}
              />
            </div>
            <Button
              size="small"
              variant="secondary"
              disabled={isAddBtnDisable}
              onClick={handleAddDebts}
              style={{ marginBottom: 24 }}
            >
              {i18next.t('add')}
            </Button>
            {debts.length > 0 && <p className={classes.size14Black}>{i18next.t('debt')}</p>}
            {debts &&
              debts.map((debt, index: number) => (
                <div key={index} className={classes.flexDocument} style={{ marginBottom: 20 }}>
                  <span style={{ marginBottom: 10 }}>
                    <FontAwesomeIcon
                      icon={faCheck}
                      size="lg"
                      style={{ color: palettes.coastal.core.mediumBlue, marginRight: 20 }}
                    />
                    {getDebtTypeTranslated(debt.type)}
                    {` ${debt.name} - ${currency.formatBrazilCurrency(debt.amount)}`}
                  </span>
                  <span
                    onClick={handleRemoveDebt(index)}
                    style={{ color: palettes.coastal.core.mediumBlue, cursor: 'pointer' }}
                  >
                    {i18next.t('remove')}
                  </span>
                </div>
              ))}
          </>
        )}
        <Textarea
          label={`${i18next.t('observations')} (${i18next.t('optional')})`}
          rows={4}
          value={observationDescription}
          onChange={handleSetObservationDescription}
        />
        {toUploadFiles ? (
          <>
            <p className={classes.size14Black}>{i18next.t('uploadDocument')}</p>
            <UploadDocument
              files={agFinancingFilesToFiles(allFiles)}
              onRemoveFile={addFileToRemove}
              onNewFiles={addNewFiles}
              progress={progress}
              replaceItem={fileReplacer}
              onUpdateFile={onUpdateFile}
            />
          </>
        ) : null}
      </DialogContent>
      <ModalActionButtons
        closeModal={closeModal}
        save={updateInfo}
        disabled={!consultationDate && isAddBtnDisable}
        changeButtonName={i18next.t('saveChanges')}
      />
    </EnhancedDialog>
  );
};
